/**
 * Header - Interactions accessibles
 * WCAG AA compliant avec focus management et keyboard navigation
 */

export class Header {
  constructor() {
    this.header = document.querySelector('.header');
    if (!this.header) return;

    this.burgerBtn = this.header.querySelector('[data-toggle="menu"]');
    this.searchBtn = this.header.querySelector('[data-toggle="search"]');
    this.searchBar = document.querySelector('#header-search');
    this.searchInput = this.searchBar?.querySelector('#search-input');
    this.nav = this.header.querySelector('#header-nav');
    this.submenuToggles = this.header.querySelectorAll('[data-submenu-toggle]');

    this.isMenuOpen = false;
    this.isSearchOpen = false;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    this.init();
  }

  init() {
    // Toggle menu burger
    if (this.burgerBtn) {
      this.burgerBtn.addEventListener('click', () => this.toggleMenu());
    }

    // Toggle recherche
    if (this.searchBtn) {
      this.searchBtn.addEventListener('click', () => this.toggleSearch());
    }

    // Sous-menus expandables
    this.submenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        // Sur mobile : empêcher navigation
        if (window.innerWidth < 1024) {
          e.preventDefault();
        }
        this.toggleSubmenu(toggle);
      });

      // Keyboard navigation pour sous-menus
      toggle.addEventListener('keydown', (e) => {
        this.handleSubmenuKeyboard(e, toggle);
      });
    });

    // Keyboard navigation globale
    document.addEventListener('keydown', (e) => this.handleGlobalKeyboard(e));

    // Fermer menu au clic en dehors
    document.addEventListener('click', (e) => {
      if (this.isMenuOpen && !this.nav.contains(e.target) && !this.burgerBtn.contains(e.target)) {
        this.closeMenu();
      }
    });

    // Gérer le resize (fermer menu mobile si passage en desktop)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 1024 && this.isMenuOpen) {
          this.closeMenu();
        }
      }, 250);
    });
  }

  // ==========================================================================
  // MENU TOGGLE
  // ==========================================================================

  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.isMenuOpen = true;

    // ARIA states
    this.burgerBtn.setAttribute('aria-expanded', 'true');
    this.burgerBtn.setAttribute('aria-label', 'Fermer le menu');
    this.nav.setAttribute('aria-hidden', 'false');

    // Bloquer scroll (mobile uniquement)
    if (window.innerWidth < 1024) {
      document.body.classList.add('menu-open');
    }

    // Focus management
    this.setupFocusTrap();

    // Annoncer au lecteur d'écran
    this.announce('Menu ouvert');
  }

  closeMenu() {
    this.isMenuOpen = false;

    // ARIA states
    this.burgerBtn.setAttribute('aria-expanded', 'false');
    this.burgerBtn.setAttribute('aria-label', 'Ouvrir le menu');
    this.nav.setAttribute('aria-hidden', 'true');

    // Débloquer scroll
    document.body.classList.remove('menu-open');

    // Fermer tous les sous-menus
    this.closeAllSubmenus();

    // Rendre le focus au bouton burger
    this.burgerBtn.focus();

    // Annoncer au lecteur d'écran
    this.announce('Menu fermé');
  }

  // ==========================================================================
  // SOUS-MENUS
  // ==========================================================================

  toggleSubmenu(toggle) {
    const menuItem = toggle.closest('.header__menu-main-item');
    const submenu = menuItem.querySelector('.header__menu-main-submenu');

    if (!submenu) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeSubmenu(toggle, submenu);
    } else {
      // Fermer les autres sous-menus d'abord
      this.closeAllSubmenus();
      this.openSubmenu(toggle, submenu);
    }
  }

  openSubmenu(toggle, submenu) {
    toggle.setAttribute('aria-expanded', 'true');
    submenu.setAttribute('aria-hidden', 'false');

    // Focus sur le premier lien du sous-menu (optionnel)
    // const firstLink = submenu.querySelector('a');
    // if (firstLink && window.innerWidth < 1024) {
    //   firstLink.focus();
    // }
  }

  closeSubmenu(toggle, submenu) {
    toggle.setAttribute('aria-expanded', 'false');
    submenu.setAttribute('aria-hidden', 'true');
  }

  closeAllSubmenus() {
    this.submenuToggles.forEach(toggle => {
      const submenu = toggle.closest('.header__menu-main-item').querySelector('.header__menu-main-submenu');
      if (submenu) {
        this.closeSubmenu(toggle, submenu);
      }
    });
  }

  // ==========================================================================
  // RECHERCHE
  // ==========================================================================

  toggleSearch() {
    if (this.isSearchOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    this.isSearchOpen = true;
    this.searchBtn.setAttribute('aria-expanded', 'true');
    this.searchBtn.setAttribute('aria-label', 'Fermer la recherche');

    if (this.searchBar) {
      this.searchBar.setAttribute('aria-hidden', 'false');
    }

    // Fermer le menu si ouvert
    if (this.isMenuOpen) {
      this.closeMenu();
    }

    // Focus sur le champ de recherche
    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 100);
    }

    this.announce('Recherche ouverte');
  }

  closeSearch() {
    this.isSearchOpen = false;
    this.searchBtn.setAttribute('aria-expanded', 'false');
    this.searchBtn.setAttribute('aria-label', 'Ouvrir la recherche');

    if (this.searchBar) {
      this.searchBar.setAttribute('aria-hidden', 'true');
    }

    // Rendre le focus au bouton recherche
    this.searchBtn.focus();

    this.announce('Recherche fermée');
  }

  // ==========================================================================
  // FOCUS MANAGEMENT (Focus Trap)
  // ==========================================================================

  setupFocusTrap() {
    // Récupérer tous les éléments focusables dans le menu
    this.focusableElements = Array.from(this.nav.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));

    if (this.focusableElements.length === 0) return;

    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

    // Focus sur le premier élément
    this.firstFocusableElement.focus();
  }

  trapFocus(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab (vers l'arrière)
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab (vers l'avant)
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  // ==========================================================================
  // KEYBOARD NAVIGATION
  // ==========================================================================

  handleGlobalKeyboard(e) {
    // Escape : fermer menu et recherche
    if (e.key === 'Escape') {
      if (this.isMenuOpen) {
        e.preventDefault();
        this.closeMenu();
      }
      if (this.isSearchOpen) {
        e.preventDefault();
        this.closeSearch();
      }
    }

    // Focus trap quand menu ouvert
    if (this.isMenuOpen && window.innerWidth < 1024) {
      this.trapFocus(e);
    }
  }

  handleSubmenuKeyboard(e, toggle) {
    const menuItem = toggle.closest('.header__menu-main-item');
    const submenu = menuItem.querySelector('.header__menu-main-submenu');

    switch (e.key) {
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        this.toggleSubmenu(toggle);
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (submenu && toggle.getAttribute('aria-expanded') === 'true') {
          // Focus sur le premier lien du sous-menu
          const firstLink = submenu.querySelector('a');
          if (firstLink) firstLink.focus();
        } else {
          this.toggleSubmenu(toggle);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (submenu && toggle.getAttribute('aria-expanded') === 'true') {
          this.closeSubmenu(toggle, submenu);
        }
        break;

      case 'Escape':
        if (submenu && toggle.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          e.stopPropagation(); // Ne pas fermer le menu principal
          this.closeSubmenu(toggle, submenu);
          toggle.focus();
        }
        break;
    }
  }

  // ==========================================================================
  // ACCESSIBILITY HELPERS
  // ==========================================================================

  /**
   * Annoncer un message aux lecteurs d'écran
   * Utilise une zone ARIA live
   */
  announce(message) {
    let liveRegion = document.getElementById('sr-live-region');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'sr-live-region';
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;

    // Nettoyer après annonce
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);
  }
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new Header());
} else {
  new Header();
}
